/** @format */

import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';

import { CookieService } from './cookie.service';
import { DCSG_APP_NAME } from '@shared/data-access/constants';
// Temporary Circular Dependency Fix
import { LoggerConfig } from './interfaces/logger-config.interface';
import { LoggerLevel } from './enums/logger-level.enum';
import { WINDOW } from './window/window.service';
import { isPlatformBrowser } from '@angular/common';

declare const BUILD_VERSION: string;

@Injectable({
  providedIn: 'root',
})
export class Logger {
  private readonly _options: LoggerConfig | null = null;
  private readonly _levels = ['TRACE', 'DEBUG', 'INFO', 'LOG', 'WARN', 'ERROR', 'OFF'];

  private readonly zone = inject(NgZone);
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly window = inject(WINDOW);
  private readonly cookieService = inject(CookieService);
  private readonly options = inject(LoggerConfig, { optional: true });
  private readonly dcsgAppName =
    inject<string>(DCSG_APP_NAME, { optional: true }) ?? 'APP NAME NOT PROVIDED';

  /**
   * @description
   * Accepts a logger level and returns the associated color for it
   */
  public static getColor(level: LoggerLevel): string | undefined {
    switch (level) {
      case LoggerLevel.TRACE:
        return 'blue';
      case LoggerLevel.DEBUG:
        return 'teal';
      case LoggerLevel.INFO:
      case LoggerLevel.LOG:
        return 'gray';
      case LoggerLevel.WARN:
        return 'orange';
      case LoggerLevel.ERROR:
        return 'red';
      case LoggerLevel.OFF:
      default:
        return undefined;
    }
  }

  /**
   * @description
   * Creates a new JavaScript Date and returns it as an ISO string
   */
  public static getTimestamp(): string {
    return new Date().toISOString();
  }

  constructor() {
    // Check to see if any options were supplied
    this._options = this.options ?? new LoggerConfig();

    /// excluding this from testing as it is not a load-bearing bit of code and the effort required to test it is far more than it is worth.
    /* istanbul ignore next */
    if (
      isPlatformBrowser(this.platformId) &&
      typeof BUILD_VERSION === 'string' &&
      BUILD_VERSION.trim() !== '' &&
      this.window !== undefined
    ) {
      // TODO: Find a more viable fix for this. Right now this is just to get rid of the BUILD_VERSION does not exist on type 'Window' error during testing

      window.BUILD_VERSION = BUILD_VERSION;
    }
  }

  /**
   * @description
   * Logs a TRACE level message
   */
  trace(message: string | any, ...additional: any[]): void {
    this._log(LoggerLevel.TRACE, true, message, additional);
  }

  /**
   * @description
   * Logs a DEBUG level message
   */
  debug(message: string | any, ...additional: any[]): void {
    this._log(LoggerLevel.DEBUG, true, message, additional);
  }

  /**
   * @description
   * Logs a INFO level message
   */
  info(message: string | any, ...additional: any[]): void {
    this._log(LoggerLevel.INFO, true, message, additional);
  }

  /**
   * @description
   * Logs a LOG level message
   */
  log(message: string | any, ...additional: any[]): void {
    this._log(LoggerLevel.LOG, true, message, additional);
  }

  /**
   * @description
   * Logs a WARN level message
   */
  warn(message: string | any, ...additional: any[]): void {
    this._log(LoggerLevel.WARN, true, message, additional);
  }

  /**
   * @description
   * Logs a ERROR level message
   */
  error(message: string | any, ...additional: any[]): void {
    this._log(LoggerLevel.ERROR, true, message, additional);
  }

  dynamicallyLog(
    requestKey: string,
    productId?: string,
    error?: HttpErrorResponse | unknown[],
    url?: string,
  ): void {
    const getURL = url ? `with URL ${productId} ` : '';
    const getProductID = productId !== '' ? `for product ${productId} ` : '';
    const errors = error ? Object.entries(error) : [];

    if (error) {
      this._log(
        LoggerLevel.ERROR,
        true,
        `[${requestKey}] API endpoint call ${getProductID} failed ${getURL}.`,
        errors,
      );
    } else {
      this._log(
        LoggerLevel.LOG,
        true,
        `[${requestKey}] API endpoint call successfully returned ${getProductID}.`,
      );
    }
  }

  /**
   * @description
   * Logs a message to server
   */
  private _logToServer(level: LoggerLevel, message: string | any, additional: any[]): void {
    // Check to see if the message should be sent server-side
    if (
      !this._options ||
      level < this._options.xhrLoggerLevel ||
      this._options.xhrLoggerUrl === undefined
    ) {
      return;
    }

    // Define HTTP Headers
    let httpHeaders = new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-LOG-REQUEST': 'true',
      'X-PROXY-REQUEST': 'true',
    });

    // Check platform
    if (!isPlatformBrowser(this.platformId)) {
      // Set a timeout on the server to prevent the response from taking too long
      httpHeaders = httpHeaders.set('X-TIMEOUT', '500');
    }

    // HTTP Options
    const httpOptions = {
      headers: httpHeaders,
    };

    // Data to post
    let data: any = {
      level: this._levels[level],
      message,
      applicationId: 'dcsg-ngx-ecommerce',
      date: Logger.getTimestamp(),
      swimLane: this.cookieService.getSwimlaneValue().toString(),
      quantumMetricSessionID: this.cookieService.get('QuantumMetricSessionID'),
      optionalParameters: additional,
      ecommApp: this.dcsgAppName,
      platform: this.platformId,
    };

    if (typeof message === 'object' && message.logType && message.logType === 'ATC') {
      data = message;
    }

    // Check to see if this has been defined
    if (typeof BUILD_VERSION === 'string' && BUILD_VERSION.trim() !== '') {
      // Set the build version on the global window
      data.BUILD_VERSION = BUILD_VERSION;
    }

    // Wrap in try / catch to prevent app from fatally failing for a log message
    try {
      // Make the XHR request
      this.http.post(this._options.xhrLoggerUrl, data, httpOptions).subscribe({
        error: (err) => {
          this._log(
            LoggerLevel.ERROR,
            false,
            `Logger._logToServer -- XHR Log Request failed: ${typeof err === 'string' ? err : JSON.stringify(err)}`,
          );
        },
      });
    } catch (exception) {
      this._log(
        LoggerLevel.ERROR,
        false,
        `Logger._logToServer -- Exception occurred during XHR Log Request: ${typeof exception === 'string' ? exception : JSON.stringify(exception)}`,
      );
    }
  }

  /**
   * @description
   * Logs message to console
   */
  private _logToConsole(level: LoggerLevel, message: string, additional: any[]): void {
    // Output the message / optional params to the console if not IE
    console.log(
      JSON.stringify({
        startTime: `${Logger.getTimestamp()}`,
        message: [message, ...additional],
        level: this._levels[level],
      }),
    );
  }

  /**
   * @description
   * Main log function that delegates to additional private methods as necessary
   */
  private _log(
    level: LoggerLevel,
    logToServer: boolean,
    message: string | any,
    additional: any[] = [],
  ): void {
    this.zone.runOutsideAngular(() => {
      // Check to see if a message has been supplied
      if (!message) {
        return;
      }

      // Allow logging on server even if client log level is off
      if (logToServer) {
        this._logToServer(level, message, additional);
      }

      // Check the log level of the client logger
      if (this._options && level < this._options.clientLoggerLevel) {
        return;
      }

      // Check the type of message to see whether it needs stringified
      if (typeof message === 'object') {
        message = JSON.stringify(message, null, 2);
      }

      this._logToConsole(level, `${message}`, additional);
    });
  }

  public getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === 'object' && error !== null) {
      // Handles plain objects, error-like objects without Error.prototype,
      // things thrown from other libraries, etc.
      try {
        return JSON.stringify(error);
      } catch {
        // circular references or other JSON.stringify failures
        return `Unstringifiable error: ${Object.prototype.toString.call(error)}`;
      }
    }

    // Strings pass through unchanged and String() works as expected on primitives
    return String(error);
  }
}
